import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  ClipboardCheck,
  FilePen,
  FileText,
  FileType,
  LayoutGrid,
  Library,
  MessagesSquare,
  PenLine,
  Shield,
} from "lucide-react";
import type { AppId } from "../../app-catalog";
import { DEFAULT_ENABLED_APPS } from "../../app-catalog";
import {
  OWNER_LOCKED_PERMISSIONS,
  PERMISSION_CATALOG,
  type PermissionMap,
  type PermissionResource,
} from "../../permission-catalog";

export const RESOURCE_LABELS: Record<PermissionResource, string> = {
  review: "Reviews",
  draft: "Draft matters",
  draftTemplate: "Draft templates",
  draftLegalRole: "Legal roles",
  draftClause: "Clause library",
  contractType: "Contract types",
  customRole: "Custom roles",
  kmWorkspace: "Workspaces",
  kmLibrary: "Libraries",
  kmDocument: "Documents",
  kmChat: "Chats",
  app: "Applications",
  integration: "Integrations",
  organization: "Organization",
  member: "Members",
  invitation: "Invitations",
  team: "Teams",
  ac: "Access control",
};

export const RESOURCE_ICONS: Partial<Record<PermissionResource, LucideIcon>> = {
  review: ClipboardCheck,
  draft: PenLine,
  draftTemplate: FilePen,
  draftLegalRole: Shield,
  draftClause: FilePen,
  contractType: FileType,
  customRole: Shield,
  kmWorkspace: BookOpen,
  kmLibrary: Library,
  kmDocument: FileText,
  kmChat: MessagesSquare,
  app: LayoutGrid,
};

export type PermissionTabKey = "application" | "contractReview" | "drafting" | "km";

export const PERMISSION_TABS: {
  key: PermissionTabKey;
  label: string;
  resources: PermissionResource[];
}[] = [
  { key: "application", label: "Application", resources: ["app"] },
  {
    key: "contractReview",
    label: "Contract review",
    resources: ["review", "contractType", "customRole"],
  },
  {
    key: "drafting",
    label: "Drafting",
    resources: ["draft", "draftTemplate", "draftLegalRole", "draftClause"],
  },
  {
    key: "km",
    label: "KM",
    resources: ["kmWorkspace", "kmLibrary", "kmDocument", "kmChat"],
  },
];

export const VISIBLE_PERMISSION_RESOURCES: PermissionResource[] = PERMISSION_TABS.flatMap(
  (tab) => tab.resources
);

const PERMISSION_TAB_REQUIRED_APP: Partial<Record<PermissionTabKey, AppId>> = {
  contractReview: "contract-review",
  km: "rag",
};

export function getVisiblePermissionTabs(
  enabledApps: AppId[] = DEFAULT_ENABLED_APPS
): typeof PERMISSION_TABS {
  return PERMISSION_TABS.filter((tab) => {
    const requiredApp = PERMISSION_TAB_REQUIRED_APP[tab.key];
    if (!requiredApp) {
      return tab.key === "application" ? enabledApps.length > 0 : true;
    }
    return enabledApps.includes(requiredApp);
  });
}

export function filterAppPermissionActions(
  actions: readonly string[],
  enabledApps: AppId[] = DEFAULT_ENABLED_APPS
): string[] {
  return actions.filter((action) => enabledApps.includes(action as AppId));
}

export function humanizeActionLabel(resource: string, action: string): string {
  if (resource === "kmWorkspace" && action === "manageMembers") {
    return "Manage members";
  }
  if (resource === "kmWorkspace" && action === "deleteDefault") {
    return "Delete default workspace";
  }
  if (
    (resource === "contractType" ||
      resource === "customRole" ||
      resource === "draftTemplate" ||
      resource === "draftLegalRole") &&
    action === "deleteSystem"
  ) {
    return "Remove system defaults";
  }
  if (resource === "kmDocument") {
    const kmDocumentLabels: Record<string, string> = {
      upload: "Upload",
      read: "View",
      regenerate: "Regenerate",
      delete: "Delete",
    };
    if (kmDocumentLabels[action]) {
      return kmDocumentLabels[action];
    }
  }
  if (resource === "app") {
    const appLabels: Record<string, string> = {
      rag: "KM",
    };
    if (appLabels[action]) {
      return appLabels[action];
    }
    return action
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }
  return action.charAt(0).toUpperCase() + action.slice(1);
}

export function totalVisibleCatalogPermissions(): number {
  return VISIBLE_PERMISSION_RESOURCES.reduce(
    (total, resource) => total + PERMISSION_CATALOG[resource].length,
    0
  );
}

export function countVisibleEnabledPermissions(permissionMap: PermissionMap): number {
  return VISIBLE_PERMISSION_RESOURCES.reduce(
    (total, resource) => total + (permissionMap[resource]?.length ?? 0),
    0
  );
}

export function totalCatalogPermissions(): number {
  return (Object.keys(PERMISSION_CATALOG) as PermissionResource[]).reduce(
    (total, resource) => total + PERMISSION_CATALOG[resource].length,
    0
  );
}

export function countEnabledPermissions(permissionMap: PermissionMap): number {
  return Object.values(permissionMap).reduce((total, actions) => total + actions.length, 0);
}

export function isOwnerLockedPermission(
  roleKey: string,
  resource: string,
  action: string
): boolean {
  if (roleKey !== "owner") return false;
  const locked = OWNER_LOCKED_PERMISSIONS[resource];
  return locked?.includes(action) ?? false;
}

export function togglePermission(
  current: PermissionMap,
  resource: string,
  action: string,
  enabled: boolean
): PermissionMap {
  const next = { ...current };
  const existing = new Set(next[resource] ?? []);
  if (enabled) {
    existing.add(action);
  } else {
    existing.delete(action);
  }
  if (existing.size > 0) {
    next[resource] = [...existing];
  } else {
    delete next[resource];
  }
  return next;
}
